import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import FormValidator from '../../services/formValidation';

/**
 * Enhanced Form component with dynamic validation support
 * Reinitializes validation whenever rules change
 */
export default function Form({
  id,
  validationRules,
  customMessages,
  submitMethod,
  onInvalidForm,
  children,
  ...rest
}) {
  const formRef = useRef(null);
  const validatorRef = useRef(null);
  const prevRulesRef = useRef(null);

  // Initialize or reinitialize validation when rules change
  const initializeValidation = useCallback(() => {
    const form = $(`#${id}`);

    // Destroy existing validator if it exists
    if (validatorRef.current) {
      form.removeData('validator');
      validatorRef.current = null;
    }

    // Setup new validation
    FormValidator.config();
    const options = { rules: validationRules };
    if (customMessages) {
      options.messages = customMessages;
    }

    // Initialize validator and store reference
    validatorRef.current = form.validate(options);
  }, [id, validationRules, customMessages]);

  // Initialize validation on mount
  useEffect(() => {
    initializeValidation();

    // Prevent form submission on Enter key
    const handleKeyDown = event => {
      if (event.key === 'Enter' && event.target.form?.id === id) {
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Cleanup validator on unmount
      if (validatorRef.current) {
        $(`#${id}`).removeData('validator');
      }
    };
  }, []);

  // Reinitialize validation when rules change
  useEffect(() => {
    const rulesChanged =
      JSON.stringify(prevRulesRef.current) !== JSON.stringify(validationRules);

    if (rulesChanged && prevRulesRef.current !== null) {
      initializeValidation();
    }

    prevRulesRef.current = validationRules;
  }, [validationRules, initializeValidation]);

  const submitHandler = useCallback(
    evt => {
      evt.preventDefault();

      const form = $(`#${id}`);

      // Ensure we have a current validator
      if (!validatorRef.current) {
        initializeValidation();
      }

      if (form.valid()) {
        submitMethod();
      } else if (onInvalidForm) {
        onInvalidForm();
      } else {
        const errorElement = document.querySelector('.error');
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start',
          });
        }
      }
    },
    [id, submitMethod, onInvalidForm, initializeValidation],
  );

  return (
    <form id={id} ref={formRef} {...rest}>
      {children(submitHandler)}
    </form>
  );
}

Form.propTypes = {
  /** Html form element id */
  id: PropTypes.string.isRequired,
  /** Validation rules as defined in the rules section of https://jqueryvalidation.org/validate/ */
  validationRules: PropTypes.shape({}).isRequired,
  customMessages: PropTypes.shape({}),
  /** Function to be called if the form is valid */
  submitMethod: PropTypes.func.isRequired,
  /** Function to be called if the form is invalid */
  onInvalidForm: PropTypes.func,
  /** The react child passed to the form is a function that receives a submitHandler that can be
   * passed as an onClick handler to a button */
  children: PropTypes.func.isRequired,
};

Form.defaultProps = {
  onInvalidForm: null,
  customMessages: null,
};
